"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
  CheckboxRow,
  Field,
  TextInput,
  ResultBanner,
  PrintButton,
  ActionPlanner,
  ActionPlanPrint,
  useSavedActions,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Pre-submission checker for a RAMS, modelled on the consolidated review
   criteria principal contractors and SSIP/HSE reviewers commonly test against
   (see docs/competitor-rams/acceptance-and-product-formula.md). Each item is a
   concrete yes/no question against the user's own document. Items the evidence
   marks as instant-rejection triggers are flagged `critical` — an unchecked
   critical drives the verdict, not the raw score, because reviewers reject on a
   single missing essential regardless of how polished the rest is. */

interface CheckItem {
  id: string;
  /** The question asked against the user's document. */
  label: string;
  /** Instant-rejection trigger — drives the verdict when unchecked. */
  critical?: boolean;
  /** One-line "why it matters" shown in the fix list. */
  why: string;
}

interface CheckSection {
  id: string;
  title: string;
  blurb: string;
  items: CheckItem[];
}

const SECTIONS: CheckSection[] = [
  {
    id: "control",
    title: "Document control & identity",
    blurb: "An unsigned, undated or unversioned document isn't a controlled record — reviewers will query it.",
    items: [
      {
        id: "control-signed",
        label: "Is it signed, dated and version-numbered by the person who prepared it?",
        critical: true,
        why: "An unsigned or undated RAMS isn't treated as a controlled document and is routinely rejected on sight.",
      },
      {
        id: "control-review",
        label: "Does it show a review date and how changes will be controlled?",
        why: "Reviewers expect a clear review trigger and change control, not a one-off document with no shelf life.",
      },
      {
        id: "control-author",
        label: "Does it name the author and their competence to write it?",
        why: "Reviewers want to see who wrote it and that they were competent to assess the work.",
      },
    ],
  },
  {
    id: "site",
    title: "Site-specific detail",
    blurb: "The single biggest rejection cause is a generic document that could describe any job on any site.",
    items: [
      {
        id: "site-named",
        label: "Does it name THIS site and full address — not a generic or previous location?",
        critical: true,
        why: "Site-specificity is the first thing reviewers look for. A generic template is a red flag to any competent reviewer.",
      },
      {
        id: "site-scope",
        label: "Is the scope precise — the exact task, location on site, dates, durations and headcount?",
        critical: true,
        why: "Reviewers reject a vague task description ('electrical installation') that doesn't prove the document was written for this specific job.",
      },
      {
        id: "site-conditions",
        label: "Does it reflect actual conditions on the ground — access routes, live services, ground conditions, plant in use?",
        why: "Hazards tied to the real workface, not a standard list, are what separate a usable RAMS from a generic one.",
      },
      {
        id: "site-visit",
        label: "Is there evidence the author saw the point of work — a site visit, photos, or a point-of-work assessment to follow?",
        why: "Reviewers doubt a RAMS that shows no sign the author has seen where the work actually happens.",
      },
      {
        id: "site-cpp",
        label: "Does it align with the site's rules, construction phase plan, permits and welfare arrangements?",
        why: "A RAMS that contradicts or ignores the site's own controls can't be coordinated safely, so it gets sent back.",
      },
    ],
  },
  {
    id: "hazards",
    title: "Hazard identification & risk scoring",
    blurb: "Hazards must be worked out step by step, with risk shown before and after controls.",
    items: [
      {
        id: "haz-perstep",
        label: "Are hazards identified systematically for each step of the task — not a single generic list?",
        critical: true,
        why: "Reviewers expect a hazard worked out per task step; a copied generic list suggests the work wasn't actually assessed.",
      },
      {
        id: "haz-residual",
        label: "Does the risk scoring show the initial AND the residual rating after controls?",
        critical: true,
        why: "Residual-only scoring is rejected — reviewers need to see the risk before controls and what's left after.",
      },
      {
        id: "haz-matrix",
        label: "Is the scoring on a recognisable matrix (likelihood × severity), so the reviewer can read it at a glance?",
        why: "The 5×5 matrix is the expected lingua franca; an unfamiliar or unexplained scale slows acceptance.",
      },
    ],
  },
  {
    id: "controls",
    title: "Control measures & hierarchy",
    blurb: "Controls must be specific and supervisable, and follow the hierarchy — not lean on PPE and good intentions.",
    items: [
      {
        id: "ctrl-specific",
        label: "Are the controls specific, measurable and supervisable — 'what, who, when' — not just 'wear PPE' and 'take care'?",
        critical: true,
        why: "Vague controls like 'take care' or 'use PPE' are explicitly rejected; reviewers need controls someone can actually supervise.",
      },
      {
        id: "ctrl-hierarchy",
        label: "Do the controls apply the hierarchy of control (eliminate / reduce / isolate / control), not rely on PPE alone?",
        critical: true,
        why: "A PPE-only RAMS is a rejection — PPE is the last resort, not the whole control strategy.",
      },
      {
        id: "ctrl-ppe",
        label: "Is PPE task-specific with the relevant EN standards and ratings, not just a generic kit list?",
        why: "Reviewers ask for PPE matched to the task with EN standards, not a one-size-fits-all list.",
      },
      {
        id: "ctrl-coshh",
        label: "If substances are used, are they linked to COSHH assessments and safety data sheets?",
        why: "Where substances are present, missing COSHH linkage and SDS is a common gap reviewers pick up.",
      },
    ],
  },
  {
    id: "method",
    title: "Method & sequence of work",
    blurb: "The method statement has to read as a workable, ordered safe system of work.",
    items: [
      {
        id: "method-sequence",
        label: "Is the method a clear, step-by-step sequence — a workable safe system of work, not a wall of text?",
        critical: true,
        why: "Reviewers expect a sequenced method they can follow; an unstructured narrative reads as a generic fill-in.",
      },
      {
        id: "method-holdpoints",
        label: "Does it set out hold points, inspections or sign-offs where work must stop and be checked?",
        why: "Hold points show the work is genuinely planned, not just described after the fact.",
      },
      {
        id: "method-interfaces",
        label: "Does it coordinate with other trades, the public and exclusion zones — the interfaces?",
        why: "Weak RAMS fail at the interfaces; reviewers check how the work fits around everyone else on site.",
      },
    ],
  },
  {
    id: "people",
    title: "People & competence",
    blurb: "Reviewers want named, competent people — and proof the workforce was involved.",
    items: [
      {
        id: "people-named",
        label: "Does it name the competent persons — supervisor plus operatives — with their cards or qualifications?",
        critical: true,
        why: "Named competence (supervisor as first point of contact, operatives with cards/quals) is a standard acceptance check.",
      },
      {
        id: "people-briefing",
        label: "Is there evidence the workforce was consulted and will be briefed, with a sign-off sheet?",
        why: "Reviewers ask whether the workforce was consulted in preparation and want briefing/attendance recorded.",
      },
    ],
  },
  {
    id: "emergency",
    title: "Emergency & high-risk arrangements",
    blurb: "The plan for when things go wrong — and the extra controls for high-risk activities.",
    items: [
      {
        id: "emerg-arrangements",
        label: "Does it set out emergency and rescue arrangements — named first aider, nearest A&E, and a task-specific rescue plan where needed?",
        critical: true,
        why: "Missing emergency/rescue arrangements (especially for confined space, height or electrical work) is a hard rejection.",
      },
      {
        id: "emerg-highrisk",
        label: "Are any high-risk activities (height, excavation, confined space, isolation, hot works, lifting, asbestos) each given their own specific controls?",
        why: "Each high-risk activity present needs its own controls; a blanket statement won't clear the gate.",
      },
      {
        id: "emerg-legislation",
        label: "Does it cite the legislation and standards relevant to the work (CDM 2015 and the activity-specific standards)?",
        why: "Reviewers expect the relevant legal and technical standards referenced, not left implicit.",
      },
    ],
  },
];

const ALL_ITEMS: CheckItem[] = SECTIONS.flatMap((s) => s.items);
const TOTAL = ALL_ITEMS.length;
const CRITICALS = ALL_ITEMS.filter((i) => i.critical);

export function RamsChecker() {
  const [docName, setDocName] = useState("");
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  // Owner/due per unchecked item — turns the fix list into an action plan.
  const [actions, setAction, clearActions] = useSavedActions("rams-checker");
  const resultTracked = useRef(false);

  const toggle = (id: string, v: boolean) =>
    setChecked((c) => ({ ...c, [id]: v }));

  const result = useMemo(() => {
    const checkedCount = ALL_ITEMS.filter((i) => checked[i.id]).length;
    const started = checkedCount > 0;
    const missedCriticals = CRITICALS.filter((i) => !checked[i.id]);
    const missedOthers = ALL_ITEMS.filter((i) => !i.critical && !checked[i.id]);
    const score = Math.round((checkedCount / TOTAL) * 100);

    let level: Verdict;
    let title: string;
    let advice: string;

    if (missedCriticals.length >= 3) {
      level = "veryhigh";
        title = "Major review gaps";
      advice =
        "Several of the essentials reviewers reject on are still missing. Close the critical gaps below before you submit — a document with these holes is unlikely to clear a principal-contractor review.";
    } else if (missedCriticals.length >= 1) {
      level = "high";
        title = "Essential review gaps";
      advice =
        "One or more of the essentials that trigger an instant rejection are still missing. Fix the critical items below first — they carry far more weight than the rest of the score.";
    } else if (missedOthers.length > 0) {
      level = "medium";
      title = "Close — tighten these before submitting";
      advice =
        "Every essential is covered. A few supporting items are still open; closing them strengthens the document before you send it for a competent-person review.";
    } else {
      level = "low";
      title = "Ready for a final competent-person review";
      advice =
        "Every check is covered. This is designed to catch common review comments — it is not a guarantee of acceptance. Acceptance is always the principal contractor's decision, so have a competent person make the final call before you submit.";
    }

    // Ranked fix list: unchecked criticals first, then unchecked supporting items.
    const fixes = [...missedCriticals, ...missedOthers];

    return {
      started,
      checkedCount,
      score,
      level,
      title,
      advice,
      missedCriticals,
      fixes,
    };
  }, [checked]);

  // Fire the funnel event once per visit when the user has started checking.
  useEffect(() => {
    if (result.started && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("rams-checker", {
        score: result.score,
        criticalsMissed: result.missedCriticals.length,
      });
    }
  }, [result]);

  const hasGaps = result.fixes.length > 0;

  const gapItems = result.fixes.map((item) => ({
    id: item.id,
    label: item.critical ? `${item.label} (essential)` : item.label,
  }));

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Checklist */}
      <div className="space-y-5">
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Check your RAMS, section by section</h2>
          <p className="mt-1 text-sm text-muted">
            Open your document alongside this and tick each item only where your RAMS genuinely covers it. Items
            marked <span className="font-semibold text-risk-veryhigh">Essential</span> are the ones reviewers
            reject documents over.
          </p>
          <div className="mt-4">
            <Field label="Document or job reference" hint="Optional — labels the printed report.">
              <TextInput
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                placeholder="e.g. Boiler replacement — 14 Mill Lane"
              />
            </Field>
          </div>
        </div>

        {SECTIONS.map((section) => {
          const done = section.items.filter((i) => checked[i.id]).length;
          return (
            <div key={section.id} className="card shadow-soft p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-semibold text-ink">{section.title}</h3>
                  <p className="mt-1 text-sm text-muted">{section.blurb}</p>
                </div>
                <span className="shrink-0 rounded-full bg-mist px-3 py-1 text-[0.78rem] font-semibold text-muted">
                  {done}/{section.items.length}
                </span>
              </div>
              <div className="mt-4 space-y-2.5">
                {section.items.map((item) => (
                  <CheckboxRow
                    key={item.id}
                    checked={!!checked[item.id]}
                    onChange={(v) => toggle(item.id, v)}
                  >
                    {item.label}
                    {item.critical && (
                      <span className="ml-2 inline-block rounded bg-[#fdecec] px-1.5 py-0.5 text-[0.68rem] font-bold uppercase tracking-wide text-[#a31616] align-middle">
                        Essential
                      </span>
                    )}
                  </CheckboxRow>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Result + printable report */}
      <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
        {result.started ? (
          <>
            <div className="print-area card shadow-soft p-6" id="rams-checker-report">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                RAMS pre-submission report
              </div>
              <h2 className="mt-1 text-lg">
                {docName ? `${docName} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={result.level} title={result.title}>
                  {result.advice}
                </ResultBanner>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[0.78rem]">
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">{result.checkedCount}/{TOTAL}</div>
                  <div className="text-muted">checks covered</div>
                </div>
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-bold text-ink">{result.score}%</div>
                  <div className="text-muted">overall</div>
                </div>
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className={`font-bold ${result.missedCriticals.length ? "text-risk-veryhigh" : "text-emerald-deep"}`}>
                    {result.missedCriticals.length}
                  </div>
                  <div className="text-muted">essentials missing</div>
                </div>
              </div>

              {hasGaps ? (
                <div className="mt-5">
                  <div className="text-[0.82rem] font-bold uppercase tracking-wide text-ink">
                    Fix these before you submit
                  </div>
                  <ol className="mt-2 space-y-2.5">
                    {result.fixes.map((item) => (
                      <li key={item.id} className="flex items-start gap-2.5 text-sm">
                        <span
                          className={`mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full ${
                            item.critical ? "bg-risk-veryhigh" : "bg-risk-medium"
                          }`}
                          aria-hidden
                        />
                        <span>
                          <span className="font-semibold text-ink">{item.label}</span>
                          {item.critical && (
                            <span className="ml-1.5 text-[0.72rem] font-bold uppercase tracking-wide text-[#a31616]">
                              Essential
                            </span>
                          )}
                          <span className="mt-0.5 block leading-relaxed text-muted">{item.why}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : (
                <p className="mt-5 text-sm leading-relaxed text-body">
                  Nothing outstanding on this checklist. Remember this catches common review comments — it does
                  not certify the document. Have a competent person make the final call.
                </p>
              )}

              <ActionPlanPrint items={gapItems} values={actions} />

              <PrintFooter toolName="RAMS pre-submission checker" />
            </div>

            <ActionPlanner items={gapItems} values={actions} onChange={setAction} onClearAll={clearActions} />

            {hasGaps && (
              <div className="card border-emerald/30 bg-emerald-tint p-5 print-hide">
                <div className="font-display text-base font-semibold text-emerald-deep">
                  Don&apos;t want to chase these gaps yourself?
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-body">
                  RamsDocs builds these sections in for you — site-specific detail, scored hazards, sequenced
                  method and the competence and emergency arrangements reviewers look for.
                </p>
                <Link
                  href="/builder/"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-deep"
                >
                  Build a RAMS draft
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}

            <div className="flex justify-end print-hide">
              <PrintButton tool="rams-checker" label="Print / save report as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Work down the checklist with your RAMS open. As soon as you start ticking, you&apos;ll see the main
            review gaps and exactly what to fix first.
          </div>
        )}
      </div>
    </div>
  );
}
